import {
  getOptjobAsObject,
  isPublishRegularWipErrorResponse,
  PUBLISH_REGULAR_WIP_ERROR_CODES,
  useLatestOptJobStatus,
  useOptJobMutation,
} from 'api/optJobs/optJobs.api'
import { postStat } from 'api/stats/stats.api'
import { optJobIsDone } from 'helpers/optimize.helper/optimize.helper'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import authStore from 'store/auth/auth'
import { useSnapshot } from 'valtio'
import { toSettlementOptimizationWindow } from './SettlementOptimizeButton.helper'

type UseSettlementOptimizationResult = {
  runSettlementOptimization: (subtypes: string[]) => void
  isRunningSettlementOptimization: boolean
}

/**
 * Resolves the display names of the settlement subtypes a conflicting opt job is already
 */
function getConflictingSubtypeDisplayNames(error: unknown, activeSystem: System | null): string[] | undefined {
  const errorBody = (error as { body?: unknown })?.body
  if (!isPublishRegularWipErrorResponse(errorBody)) return undefined
  if (errorBody.error.code !== PUBLISH_REGULAR_WIP_ERROR_CODES.OPT_JOB_ALREADY_RUNNING) return undefined

  const conflictingSubtypes = errorBody.error.details?.conflicting_subtypes
  if (!conflictingSubtypes || conflictingSubtypes.length === 0) return undefined

  const settlementSubtypes = activeSystem?.subtypes?.settlement ?? []
  return conflictingSubtypes.map(
    (subtype) => settlementSubtypes.find((s) => s.subtype === subtype)?.subtype_display_name ?? subtype
  )
}

/**
 * Creates the settlement optimization job for the period selected in the follow-up header's
 * date pickers. One job carries every selected scenario; the backend starts one optimization
 * per subtype.
 */
export function useSettlementOptimization(period: Period): UseSettlementOptimizationResult {
  const { t } = useTranslation()
  const authSnap = useSnapshot(authStore)
  const { mutate: optJobCreate, isPending: isCreatingOptJob } = useOptJobMutation()
  const { data: latestOptJob, status: fetchingLatestJobStatus } = useLatestOptJobStatus({ type: `settlement` })

  const isRunningSettlementOptimization =
    isCreatingOptJob || fetchingLatestJobStatus === `pending` || !optJobIsDone(getOptjobAsObject(latestOptJob))

  function runSettlementOptimization(subtypes: string[]): void {
    const { startTime, endTime } = toSettlementOptimizationWindow(period)

    optJobCreate(
      {
        opt_model: authSnap.optModelId,
        opt_job_type: 'settlement',
        input_data: {
          opt_start_time: startTime,
          opt_end_time: endTime,
          // An omitted include_subtypes makes the backend run every subtype that allows manual
          // triggering, so an empty selection is sent as no field at all rather than as a list
          // of all scenarios — the backend, not the client, decides what "all" covers.
          ...(subtypes.length > 0 && { include_subtypes: subtypes }),
        },
      },
      {
        onSuccess: () => {
          alertStore.success(
            t(
              `The settlement optimization has been successfully created. You can view the status in the notification log.`
            )
          )

          postStat(`settlement-optimize-button`, `run-settlement-optjob`)
        },
        // A subtype key on its own ('heat') means nothing to a user — replace the mutation's
        // generic error alert with one naming the subtypes by display name. Runs after the
        // mutation's own onError, so it overwrites that alert only when this error matches.
        onError: (error) => {
          const conflictingSubtypeNames = getConflictingSubtypeDisplayNames(error, authSnap.activeSystem)
          if (conflictingSubtypeNames) {
            alertStore.error(
              t(
                `An optimization is already running for {{subtypes}}. Wait for it to finish before starting a new one.`,
                { subtypes: conflictingSubtypeNames.join(', ') }
              )
            )
          }
        },
      }
    )
  }

  return { runSettlementOptimization, isRunningSettlementOptimization }
}
